// ============================================================
// DeletionRequestsPage.js (admin portal)
// The A2Z IT review queue for RM/NI-raised care-record deletion requests.
//
// NOTHING ON THIS PAGE DELETES DATA. Approving a request records the
// decision; the physical deletion is a manual A2Z IT action (client
// conversation + existing tooling) recorded afterwards with "Mark
// actioned". The handler family contains no DELETE by design, and the
// page says so, because a button named "Approve" on a deletion queue
// otherwise reads as the destructive act itself.
//
// A CARD LIST rather than DataTable (the ContactMessages precedent):
// the RM's reason is the content, and a cell would truncate the only
// part worth reading. Queue is served OLDEST-first (worked top-down).
//
// A rejection REQUIRES notes — the server 400s without them, because a
// rejection with no explanation is unanswerable for the RM who raised it.
// The UI enforces it before the wire does.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Badge from '../components/Badge';
import { toast } from '../components/Toaster';
import {
  listAdminDeletionRequests,
  reviewAdminDeletionRequest,
  markAdminDeletionRequestActioned,
} from '../api/adminDeletionRequests';
import { translateError } from '../utils/errorMessages';
import { formatDateTime } from '../utils/format';
import StatusMessage from '../components/StatusMessage';
import './DeletionRequestsPage.css';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const STATUS_BADGE = { pending: 'warning', approved: 'success', rejected: 'danger' };

export default function DeletionRequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Default to the queue that needs working — pending.
  const urlStatus = searchParams.get('status') ?? 'pending';

  const [items, setItems] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await listAdminDeletionRequests({ status: urlStatus || undefined });
      setItems(res.items || []);
      setTruncated(!!res.truncated);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [urlStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === '') next.delete('status'); else next.set('status', value);
    setSearchParams(next, { replace: true });
  };

  const act = async (id, fn, successMessage) => {
    setBusyId(id);
    try {
      await fn();
      toast.success(successMessage);
      setNoteDrafts((d) => ({ ...d, [id]: '' }));
      await fetchData();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setBusyId(null);
    }
  };

  const review = (item, decision) => {
    const notes = (noteDrafts[item.deletionRequestId] || '').trim();
    if (decision === 'rejected' && !notes) {
      toast.error('A rejection must carry notes - the RM who raised it needs to know why.');
      return;
    }
    act(
      item.deletionRequestId,
      () => reviewAdminDeletionRequest(item.deletionRequestId, decision, notes || undefined),
      decision === 'approved'
        ? 'Approved. Nothing is deleted yet - carry out the deletion manually, then Mark actioned.'
        : 'Rejected. The RM will see the notes against their request.'
    );
  };

  const markActioned = (item) => {
    act(
      item.deletionRequestId,
      () => markAdminDeletionRequestActioned(item.deletionRequestId),
      'Recorded as actioned.'
    );
  };

  const card = (item) => {
    const busy = busyId === item.deletionRequestId;
    return (
      <article key={item.deletionRequestId} className="delreq__card" data-testid="deletion-request-card">
        <header className="delreq__head">
          <div>
            <span className="delreq__client">{item.clientName}</span>
            <span className="delreq__type">{item.entityType.replace(/_/g, ' ')}</span>
          </div>
          <div className="delreq__badges">
            <Badge variant={STATUS_BADGE[item.status] || 'neutral'}>{item.status}</Badge>
            {item.isActioned && <Badge variant="teal">actioned</Badge>}
          </div>
        </header>
        <p className="delreq__reason">{item.reason}</p>
        <p className="delreq__meta">
          Requested by {item.requestedBy} · {formatDateTime(item.requestedAt)} · Record{' '}
          <code className="delreq__id">{item.entityId}</code>
        </p>
        {item.reviewedAt && (
          <p className="delreq__meta">
            Reviewed by {item.reviewedBy} · {formatDateTime(item.reviewedAt)}
            {item.reviewNotes ? <> · {item.reviewNotes}</> : null}
          </p>
        )}
        {item.isActioned && item.actionedAt && (
          <p className="delreq__meta">Actioned by {item.actionedBy} · {formatDateTime(item.actionedAt)}</p>
        )}

        {item.status === 'pending' && (
          <div className="delreq__actions">
            <textarea
              className="delreq__notes"
              placeholder="Decision notes (required for a rejection)"
              value={noteDrafts[item.deletionRequestId] || ''}
              onChange={(e) => setNoteDrafts((d) => ({ ...d, [item.deletionRequestId]: e.target.value }))}
              rows={2}
              disabled={busy}
            />
            <div className="delreq__buttons">
              <button
                type="button"
                className="button-primary"
                onClick={() => review(item, 'approved')}
                disabled={busy}
              >
                Approve
              </button>
              <button
                type="button"
                className="button-secondary"
                onClick={() => review(item, 'rejected')}
                disabled={busy}
              >
                Reject
              </button>
            </div>
          </div>
        )}
        {item.status === 'approved' && !item.isActioned && (
          <div className="delreq__actions">
            <p className="delreq__actioned-hint">
              Approved - the deletion is carried out manually. Record it here once done.
            </p>
            <button
              type="button"
              className="button-secondary"
              onClick={() => markActioned(item)}
              disabled={busy}
            >
              Mark actioned
            </button>
          </div>
        )}
      </article>
    );
  };

  return (
    <>
      <PageHeader
        title="Deletion Requests"
        subtitle="RM/NI-raised care-record deletion requests. Approving deletes nothing - the deletion is carried out manually and recorded here afterwards."
      />

      <div className="clients-page__toolbar">
        <div className="clients-page__filters">
          <label className="clients-page__tenant-filter">
            <span className="clients-page__tenant-label">Status</span>
            <select
              value={urlStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="clients-page__tenant-select"
              data-testid="deletion-status-filter"
            >
              <option value="">All</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {loading && <StatusMessage as="p" variant="loading" className="delreq__state">Loading deletion requests…</StatusMessage>}
      {error && (
        <StatusMessage as="p" variant="error" className="delreq__state delreq__state--error">
          {error} <button type="button" className="delreq__retry" onClick={fetchData}>Retry</button>
        </StatusMessage>
      )}

      {!loading && !error && truncated && (
        <StatusMessage as="p" variant="empty" className="delreq__state">
          Showing the oldest {items.length} - newer requests exist beyond this page.
        </StatusMessage>
      )}

      {!loading && !error && items.length === 0 && (
        <StatusMessage as="p" variant="empty" className="delreq__state">
          {urlStatus ? 'No requests with that status.' : 'No deletion requests have been raised.'}
        </StatusMessage>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="delreq__list">{items.map(card)}</div>
      )}
    </>
  );
}
